import React, { useCallback, useEffect, useState } from 'react';
import { Paper, Box, Typography, TextField, MenuItem, Button, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Chip, Autocomplete, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../utils/apiClient';
import { API_BASE as API_BASE_URL } from '../utils/apiConfig';

// Мощность для счёта: правило «площадка + модель» и ручная поправка на асике.
// Сам выбор мощности — на сервере (device_billing_power), здесь только настройка.
const emptyDraft = { oc_from_ths: '', oc_power_w: '', stock_power_w: '' };
const draftFrom = (rule) => (rule ? { oc_from_ths: rule.oc_from_ths, oc_power_w: rule.oc_power_w, stock_power_w: rule.stock_power_w ?? '' } : emptyDraft);

export default function BillingPowerCard({ defaultMonth }) {
  const { t } = useTranslation('billing');
  const [hostings, setHostings] = useState([]);
  const [hostingId, setHostingId] = useState('');
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [message, setMessage] = useState(null);
  const [device, setDevice] = useState(null);
  const [devicePower, setDevicePower] = useState('');
  const [month, setMonth] = useState(defaultMonth || '');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  // пусто — весь месяц; день — правило поменяли в середине месяца, дни до него не трогаем
  const [fromDay, setFromDay] = useState('');

  useEffect(() => {
    api.get(`${API_BASE_URL}hosting_power/hostings`).then((r) => {
      const list = r.data?.hostings || [];
      setHostings(list);
      if (list.length) setHostingId(list[0].id);
    }).catch(() => setHostings([]));
  }, []);

  const load = useCallback(async () => {
    if (!hostingId) return;
    try {
      const r = await api.get(`${API_BASE_URL}hosting_power`, { params: { hosting_id: hostingId } });
      setData(r.data);
      setDrafts(Object.fromEntries((r.data.models || []).map((m) => [m.model_id, draftFrom(m.rule)])));
    } catch (e) {
      setData(null);
      setMessage({ severity: 'error', text: t('powerLoadError') });
    }
  }, [hostingId, t]);
  useEffect(() => { load(); }, [load]);

  const setDraft = (modelId, field, value) => setDrafts((d) => ({ ...d, [modelId]: { ...d[modelId], [field]: value } }));

  const run = async (action, ok) => {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      setMessage({ severity: 'success', text: ok });
      await load();
    } catch (e) {
      setMessage({ severity: 'error', text: e?.response?.data?.error || t('powerSaveError') });
    } finally {
      setBusy(false);
    }
  };

  const saveRule = (modelId) => run(() => api.put(`${API_BASE_URL}hosting_power`, { hosting_id: hostingId, model_id: modelId, ...drafts[modelId] }), t('powerSaved'));
  const deleteRule = (modelId) => run(() => api.delete(`${API_BASE_URL}hosting_power`, { params: { hosting_id: hostingId, model_id: modelId } }), t('powerSaved'));
  const saveDevice = (id, power) => run(() => api.put(`${API_BASE_URL}devices/${id}/billing_power`, { power_w: power }), t('powerSaved')).then(() => { setDevice(null); setDevicePower(''); });
  // Пересчёт в два шага: сначала предпросмотр (что изменится), потом подтверждение
  const showPreview = async () => {
    setBusy(true);
    setMessage(null);
    setPreview(null);
    try {
      const r = await api.post(`${API_BASE_URL}hosting_power/recalculate`, { hosting_id: hostingId, month, apply: false, from_day: fromDay || undefined });
      setPreview(r.data);
    } catch (e) {
      setMessage({ severity: 'error', text: e?.response?.data?.error || t('powerSaveError') });
    } finally {
      setBusy(false);
    }
  };
  const applyRecalc = async () => {
    setPreview(null);
    setBusy(true);
    setMessage(null);
    try {
      await api.post(`${API_BASE_URL}hosting_power/recalculate`, { hosting_id: hostingId, month, apply: true, from_day: fromDay || undefined });
      setMessage({ severity: 'success', text: t('powerRecalculated') });
      await load();
    } catch (e) {
      const blocked = e.response?.data?.code === 'MONTH_HAS_BILLS';
      setMessage({ severity: 'error', text: blocked ? t('powerRecalcBlocked', { count: e.response.data.bills }) : (e?.response?.data?.error || t('powerSaveError')) });
    } finally {
      setBusy(false);
    }
  };

  if (!hostings.length) return null;
  const overrides = (data?.devices || []).filter((d) => d.billing_power_w != null);

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>{t('powerTitle')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('powerHint')}</Typography>
      {hostings.length > 1 && (
        <TextField select size="small" label={t('powerHosting')} value={hostingId} onChange={(e) => { setPreview(null); setHostingId(e.target.value); }} sx={{ minWidth: 260, mb: 2 }}>
          {hostings.map((h) => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
        </TextField>
      )}
      {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 860 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('powerModel')}</TableCell>
              <TableCell>{t('powerProfiles')}</TableCell>
              <TableCell>{t('powerNow')}</TableCell>
              <TableCell>{t('powerOcFrom')}</TableCell>
              <TableCell>{t('powerOcWatts')}</TableCell>
              <TableCell>{t('powerStockWatts')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.models || []).map((m) => (
              <TableRow key={m.model_id}>
                <TableCell>{m.model || '—'}<Typography variant="caption" color="text.secondary" display="block">{t('powerDevices', { count: m.devices })}</Typography></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {m.profiles.map((p) => <Chip key={p.ths} size="small" label={`${p.ths} TH × ${p.count}`} />)}
                    {m.unknown_profile > 0 && <Chip size="small" variant="outlined" label={t('powerUnknownProfile', { count: m.unknown_profile })} />}
                  </Box>
                </TableCell>
                <TableCell>
                  {m.power.map((p) => <Typography key={`${p.power_w}${p.source}`} variant="body2" noWrap>{p.power_w ?? '—'} Вт × {p.count} <Typography component="span" variant="caption" color="text.secondary">{t(`powerSource_${p.source}`)}</Typography></Typography>)}
                </TableCell>
                {['oc_from_ths', 'oc_power_w', 'stock_power_w'].map((f) => (
                  <TableCell key={f}>
                    <TextField size="small" type="number" sx={{ width: 100 }} value={drafts[m.model_id]?.[f] ?? ''} onChange={(e) => setDraft(m.model_id, f, e.target.value)} />
                  </TableCell>
                ))}
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Button size="small" disabled={busy || !drafts[m.model_id]?.oc_from_ths || !drafts[m.model_id]?.oc_power_w} onClick={() => saveRule(m.model_id)}>{t('powerSave')}</Button>
                  {m.rule && <Button size="small" color="inherit" disabled={busy} onClick={() => deleteRule(m.model_id)}>{t('powerRemove')}</Button>}
                </TableCell>
              </TableRow>
            ))}
            {data && (data.models || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    {(data.devices || []).length > 0 ? t('powerNoModels', { count: data.devices.length }) : t('powerNoDevices')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>{t('powerOverrideTitle')}</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
        <Autocomplete
          size="small"
          sx={{ minWidth: 300 }}
          options={data?.devices || []}
          value={device}
          onChange={(e, v) => setDevice(v)}
          getOptionLabel={(d) => `${d.ip || '—'}${d.serial_number ? ` · ${d.serial_number}` : ''}`}
          isOptionEqualToValue={(a, b) => a.device_id === b.device_id}
          renderInput={(params) => <TextField {...params} label={t('powerDevice')} />}
        />
        <TextField size="small" type="number" label={t('powerWatts')} value={devicePower} onChange={(e) => setDevicePower(e.target.value)} sx={{ width: 130 }} />
        <Button variant="outlined" disabled={busy || !device || !devicePower} onClick={() => saveDevice(device.device_id, Number(devicePower))}>{t('powerSave')}</Button>
      </Box>
      {overrides.map((d) => (
        <Chip key={d.device_id} sx={{ mr: 0.5, mb: 0.5 }} label={`${d.ip}: ${d.billing_power_w} Вт`} onDelete={() => saveDevice(d.device_id, null)} />
      ))}

      <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5 }}>{t('powerRecalcTitle')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('powerRecalcHint')}</Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField size="small" type="month" label={t('workHoursMonth')} value={month} onChange={(e) => { setPreview(null); setFromDay(''); setMonth(e.target.value); }} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label={t('powerRecalcFromDay')} value={fromDay} disabled={!month}
          onChange={(e) => { setPreview(null); setFromDay(e.target.value); }} InputLabelProps={{ shrink: true }}
          inputProps={month ? { min: `${month}-01`, max: `${month}-${String(new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate()).padStart(2, '0')}` } : {}} />
        <Button variant="outlined" disabled={busy || !month} onClick={showPreview}>{t('powerRecalcPreview')}</Button>
      </Box>
      {preview && (
        <Alert severity={preview.bills > 0 ? 'warning' : 'info'} sx={{ mt: 2 }} onClose={() => setPreview(null)}>
          {preview.bills > 0
            ? t('powerRecalcBlocked', { count: preview.bills })
            : preview.rows === 0
              ? t('powerRecalcNothing')
              : t('powerRecalcSummary', { rows: preview.rows, devices: preview.devices, kwhBefore: preview.kwh_before, kwhAfter: preview.kwh_after, amountBefore: preview.amount_before, amountAfter: preview.amount_after })}
          {preview.bills === 0 && preview.rows > 0 && (
            <Box sx={{ mt: 1 }}><Button size="small" variant="contained" disabled={busy} onClick={applyRecalc}>{t('powerRecalc')}</Button></Box>
          )}
        </Alert>
      )}
    </Paper>
  );
}
